/**
 * 3D Coverflow Product Carousel
 * Positions cards around a shallow 3D wheel: active card centered/full-size,
 * neighbors rotated + pushed back + scaled down + dimmed, falling off further
 * out. Next/prev steps the active index and the CSS transition (defined in
 * coverflow-collection.css) eases every card to its new transform at once,
 * which reads as the whole wheel turning.
 */
(function () {
  function initCoverflow(root) {
    if (!root || root.dataset.cfInitialized) return;
    root.dataset.cfInitialized = 'true';

    var stage = root.querySelector('[data-coverflow-stage]');
    var track = root.querySelector('[data-coverflow-track]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-coverflow-card]'));
    var prevBtn = root.querySelector('[data-coverflow-prev]');
    var nextBtn = root.querySelector('[data-coverflow-next]');
    var dotsWrap = root.querySelector('[data-coverflow-dots]');

    if (!stage || !track || cards.length === 0) return;

    var count = cards.length;
    var initialIndex = parseInt(root.dataset.initialIndex, 10);
    if (isNaN(initialIndex) || initialIndex < 0 || initialIndex >= count) {
      initialIndex = 0;
    }
    var activeIndex = initialIndex;
    var isAnimating = false;

    // Tuning for the 3D wheel. Adjust these to taste.
    var STEP_ANGLE = 32; // degrees each side card rotates, per step away from center
    var STEP_X = 0.62; // fraction of card width to offset each step away
    var STEP_Z = 140; // px pushed back into the screen per step away
    var STEP_SCALE = 0.16; // scale reduction per step away
    var MAX_VISIBLE_STEPS = 3; // cards further than this fade out completely

    // Build dots
    dotsWrap.innerHTML = '';
    var dots = cards.map(function (_, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'coverflow-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.addEventListener('click', function () {
        goTo(i);
      });
      dotsWrap.appendChild(dot);
      return dot;
    });

    function shortestDelta(from, to, n) {
      // shortest circular distance from `from` to `to` among n positions
      var raw = to - from;
      var wrapped = ((raw % n) + n) % n;
      if (wrapped > n / 2) wrapped -= n;
      return wrapped;
    }

    function render() {
      cards.forEach(function (card, i) {
        var delta = shortestDelta(activeIndex, i, count);
        var absDelta = Math.abs(delta);
        var sign = delta === 0 ? 0 : delta > 0 ? 1 : -1;

        var cardWidth = parseFloat(getComputedStyle(root).getPropertyValue('--cf-card-w')) || 320;

        var x = sign * absDelta * cardWidth * STEP_X;
        var z = -absDelta * STEP_Z;
        var rot = -sign * Math.min(absDelta, MAX_VISIBLE_STEPS) * STEP_ANGLE;
        var scale = Math.max(1 - absDelta * STEP_SCALE, 0.45);
        var opacity = absDelta > MAX_VISIBLE_STEPS ? 0 : absDelta === 0 ? 1 : Math.max(1 - absDelta * 0.32, 0.18);
        var brightness = absDelta === 0 ? 1 : Math.max(1 - absDelta * 0.18, 0.45);
        var zIndex = 100 - absDelta;

        card.style.setProperty('--cf-x', x + 'px');
        card.style.setProperty('--cf-z', z + 'px');
        card.style.setProperty('--cf-rot', rot + 'deg');
        card.style.setProperty('--cf-scale', scale);
        card.style.setProperty('--cf-opacity', opacity);
        card.style.setProperty('--cf-brightness', brightness);
        card.style.setProperty('--cf-zindex', zIndex);
        card.setAttribute('data-active', i === activeIndex ? 'true' : 'false');
        card.setAttribute('aria-hidden', absDelta > MAX_VISIBLE_STEPS ? 'true' : 'false');

        var link = card.querySelector('[data-coverflow-link]');
        if (link) {
          link.setAttribute('tabindex', i === activeIndex ? '0' : '-1');
        }
      });

      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
      });
    }

    function goTo(index) {
      if (isAnimating) return;
      var n = ((index % count) + count) % count;
      if (n === activeIndex) {
        // already active and centered: treat as "open product"
        navigateToActive();
        return;
      }
      activeIndex = n;
      isAnimating = true;
      render();
      window.clearTimeout(root._cfAnimTimer);
      root._cfAnimTimer = window.setTimeout(function () {
        isAnimating = false;
      }, 650);
    }

    function navigateToActive() {
      var activeCard = cards[activeIndex];
      var link = activeCard && activeCard.querySelector('[data-coverflow-link]');
      if (link && link.getAttribute('href')) {
        window.location.href = link.getAttribute('href');
      }
    }

    function next() {
      goTo(activeIndex + 1);
    }
    function prev() {
      goTo(activeIndex - 1);
    }

    cards.forEach(function (card, i) {
      card.addEventListener('click', function (e) {
        if (i !== activeIndex) {
          e.preventDefault();
          goTo(i);
        } else {
          // Active card: if the click landed on the real link, let it
          // navigate natively (respects cmd/ctrl-click, middle-click, etc).
          // Any other click on the active card triggers the same navigation.
          var link = card.querySelector('[data-coverflow-link]');
          var clickedLink = e.target === link || (link && link.contains(e.target));
          if (!clickedLink) {
            e.preventDefault();
            navigateToActive();
          }
        }
      });
    });

    if (nextBtn) nextBtn.addEventListener('click', next);
    if (prevBtn) prevBtn.addEventListener('click', prev);

    // Keyboard support
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        navigateToActive();
      }
    });

    // Drag / swipe support (pointer events cover mouse + touch + pen)
    var dragState = null;
    var DRAG_THRESHOLD = 40; // px before a drag counts as a swipe
    var MOVE_THRESHOLD = 8; // px before we even consider it a drag (vs a click)
    var DIRECTION_LOCK_THRESHOLD = 6; // px before we commit to horizontal vs vertical

    stage.addEventListener('pointerdown', function (e) {
      dragState = {
        startX: e.clientX,
        startY: e.clientY,
        lastX: e.clientX,
        moved: false,
        pointerId: e.pointerId,
        captured: false,
        direction: null // becomes 'horizontal' or 'vertical' once decided
      };
    });

    stage.addEventListener(
      'pointermove',
      function (e) {
        if (!dragState) return;
        var dx = e.clientX - dragState.startX;
        var dy = e.clientY - dragState.startY;

        // Decide gesture direction once, as early as possible. Until then,
        // do nothing special and let the browser make its own choice too.
        if (!dragState.direction) {
          if (Math.abs(dx) > DIRECTION_LOCK_THRESHOLD || Math.abs(dy) > DIRECTION_LOCK_THRESHOLD) {
            dragState.direction = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
          }
        }

        if (dragState.direction === 'horizontal') {
          // Committed to a swipe: stop the browser from also scrolling the
          // page on this same gesture, even if the finger drifts vertically
          // partway through.
          e.preventDefault();
          if (Math.abs(dx) > MOVE_THRESHOLD) {
            dragState.moved = true;
            if (!dragState.captured && stage.setPointerCapture) {
              stage.setPointerCapture(e.pointerId);
              dragState.captured = true;
            }
          }
        }
        // If direction is 'vertical' (or not yet decided), do nothing —
        // native page scroll is left completely alone.

        dragState.lastX = e.clientX;
      },
      { passive: false }
    );

    stage.addEventListener('pointerup', function (e) {
      if (!dragState) return;
      var dx = e.clientX - dragState.startX;
      if (dragState.direction === 'horizontal' && dragState.moved && Math.abs(dx) > DRAG_THRESHOLD) {
        if (dx < 0) next();
        else prev();
      }
      if (dragState.captured && stage.releasePointerCapture) {
        try {
          stage.releasePointerCapture(dragState.pointerId);
        } catch (err) {
          /* already released */
        }
      }
      dragState = null;
    });

    stage.addEventListener('pointercancel', function () {
      dragState = null;
    });

    // Prevent native image drag ghosting from interfering
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    // Mouse wheel (horizontal intent) support, gently debounced
    var wheelLock = false;
    stage.addEventListener(
      'wheel',
      function (e) {
        var horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
        if (!horizontal) return;
        e.preventDefault();
        if (wheelLock) return;
        wheelLock = true;
        if (e.deltaX > 0) next();
        else prev();
        window.setTimeout(function () {
          wheelLock = false;
        }, 350);
      },
      { passive: false }
    );

    render();
  }

  window.initCoverflow = initCoverflow;

  // Auto-init any already on the page (in case the inline loader missed it,
  // e.g. on Shopify section reorder in the theme editor)
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-coverflow]').forEach(initCoverflow);
  });
})();